import { DatePipe, CommonModule, DOCUMENT } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  inject,
  signal,
  computed,
  effect
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  BreakpointObserver,
  Breakpoints,
  BreakpointState,
} from '@angular/cdk/layout';
import { AuthService } from 'src/app/auth/auth.service';
import { LanguageService } from 'src/app/core/services/language.service';
import {
  FormBuilder,
  FormControl,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule
} from '@angular/forms';
import { ValidatorsService } from 'src/app/services/validators.service';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { ListBranch } from 'src/app/auth/auth.interface';
import { ParametricService } from '../../services/parametric.service';
import { PointOfSaleService } from "../../pages/management/services/point-of-sale.service";
import { sId } from "../../shared/interfaces/api.interfaces";
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { SelectModule } from 'primeng/select';
import { EventService } from 'src/app/core/services/event.service';
import { ProfileService } from 'src/app/pages/management/services/profile.service';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    BsDropdownModule,
    SelectModule
  ],
  templateUrl: './topbar.component.html',
  styles: [],
  providers: [DatePipe],
})
export class TopbarComponent implements OnInit {
  element: any;
  cookieValue: any;
  flagvalue: any;
  countryName: any;
  valueset: any;

  document = inject(DOCUMENT);
  router = inject(Router);
  languageService = inject(LanguageService);
  translate = inject(TranslateService);
  authService = inject(AuthService);
  breakpointObserver = inject(BreakpointObserver);
  parametricService = inject(ParametricService);
  pointOfSaleService = inject(PointOfSaleService);
  datePipe = inject(DatePipe);
  fb = inject(FormBuilder);
  validatorsService = inject(ValidatorsService);
  eventService = inject(EventService);

  currentTheme = signal<string>(localStorage.getItem('themeMode') || 'light');
  openMobileMenu: boolean = false;

  @Output() settingsButtonClicked = new EventEmitter();
  @Output() mobileMenuButtonClicked = new EventEmitter();
  cities: any[] | undefined;
  selectedCity: any | undefined;
  isPageViewMovil: boolean = false;
  styleSucursales = signal({});

  form: UntypedFormGroup = this.fb.group({
    id_sucursal: ['', [Validators.required]],
  });
  sucursales = signal<ListBranch[]>([]);
  isReloadSub$!: Subscription;

  get dataCufd() {
    return this.authService._cufd();
  }

  logoUrl: string | null = null;
  hasLogoError: boolean = false;

  profileService = inject(ProfileService);

  constructor() {
    effect(() => {
      const branches = this.validatorsService.listBranch();
      if (branches && branches.length > 0) {
        this.getAllSucursales();
      }
    });
  }

  isSuperAdmin = computed(() => {
    return this.validatorsService.user()?.role?.name === 'SUPER_ADMIN';
  });

  isSuspended = computed(() => {
    return !!this.validatorsService.company()?.customerSubscription?.isSuspended;
  });

  // ─── 1. SUSCRIPCIÓN COMERCIAL ────────────────────────────────
  rawSubExpirationDate = computed(() => {
    return this.validatorsService.company()?.customerSubscription?.expirationDate || null;
  });

  subExpirationDate = computed(() => {
    const raw = this.rawSubExpirationDate();
    if (!raw) return null;
    return new Date(raw);
  });

  subDaysRemaining = computed<number | null>(() => {
    const raw = this.rawSubExpirationDate();
    if (!raw) return null;
    const expStr = typeof raw === 'string' ? raw.substring(0, 10) : new Date(raw).toISOString().substring(0, 10);
    const parts = expStr.split('-').map(Number);
    if (parts.length !== 3) return null;
    
    const expDate = new Date(parts[0], parts[1] - 1, parts[2]);
    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const diffTime = expDate.getTime() - todayDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  });

  subAlertLevel = computed<'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL' | null>(() => {
    if (this.isSuperAdmin()) return null;
    if (this.isSuspended()) return 'CRITICAL';
    const days = this.subDaysRemaining();
    if (days === null) return null;
    if (days <= 0) return 'CRITICAL';
    if (days <= 5) return 'URGENT';
    if (days <= 15) return 'WARNING';
    if (days <= 30) return 'INFO';
    return null;
  });

  subAlertDismissed = signal(false);

  // ─── 2. TOKEN DELEGADO SIAT ──────────────────────────────────
  rawSiatTokenEndDate = computed(() => {
    return this.validatorsService.company()?.tokenEndDate || null;
  });

  siatTokenEndDate = computed(() => {
    const raw = this.rawSiatTokenEndDate();
    if (!raw) return null;
    return new Date(raw);
  });

  siatTokenDaysRemaining = computed<number | null>(() => {
    const raw = this.rawSiatTokenEndDate();
    if (!raw) return null;
    const expStr = typeof raw === 'string' ? raw.substring(0, 10) : new Date(raw).toISOString().substring(0, 10);
    const parts = expStr.split('-').map(Number);
    if (parts.length !== 3) return null;
    
    const expDate = new Date(parts[0], parts[1] - 1, parts[2]);
    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const diffTime = expDate.getTime() - todayDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  });

  siatTokenAlertLevel = computed<'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL' | null>(() => {
    if (this.isSuperAdmin()) return null;
    const days = this.siatTokenDaysRemaining();
    if (days === null) return null;
    if (days <= 0) return 'CRITICAL';
    if (days <= 5) return 'URGENT';
    if (days <= 15) return 'WARNING';
    return null; // Notificaciones a partir de 15 días
  });

  siatTokenAlertDismissed = signal(false);

  // ─── 3. FIRMA DIGITAL (SOLO ELECTRONIC_ONLINE) ───────────────
  isElectronicModality = computed(() => {
    return this.validatorsService.company()?.modality === 'ELECTRONIC_ONLINE';
  });

  certEndDate = computed(() => {
    if (!this.isElectronicModality()) return null;
    const endStr = this.profileService.certificate()?.endDate;
    if (!endStr) return null;
    return new Date(endStr);
  });

  certDaysRemaining = computed<number | null>(() => {
    if (!this.isElectronicModality()) return null;
    const endStr = this.profileService.certificate()?.endDate;
    if (!endStr) return null;
    const expStr = typeof endStr === 'string' ? endStr.substring(0, 10) : new Date(endStr).toISOString().substring(0, 10);
    const parts = expStr.split('-').map(Number);
    if (parts.length !== 3) return null;

    const expDate = new Date(parts[0], parts[1] - 1, parts[2]);
    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const diffTime = expDate.getTime() - todayDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  });

  certAlertLevel = computed<'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL' | null>(() => {
    if (this.isSuperAdmin() || !this.isElectronicModality()) return null;
    const days = this.certDaysRemaining();
    if (days === null) return null;
    if (days <= 0) return 'CRITICAL';
    if (days <= 5) return 'URGENT';
    if (days <= 15) return 'WARNING';
    return null;
  });

  certAlertDismissed = signal(false);

  // ─── 4. ALERTAS ACTIVAS Y PRIORIZADAS (GLOBAL) ───────────────
  activeAlerts = computed(() => {
    if (this.isSuperAdmin()) return [];

    const certLvl = this.certAlertLevel();
    const certDays = this.certDaysRemaining();
    const showCert = certLvl && (!this.certAlertDismissed() || certLvl === 'CRITICAL' || certLvl === 'URGENT');

    const tokenLvl = this.siatTokenAlertLevel();
    const tokenDays = this.siatTokenDaysRemaining();
    const showToken = tokenLvl && (!this.siatTokenAlertDismissed() || tokenLvl === 'CRITICAL' || tokenLvl === 'URGENT');

    const subLvl = this.subAlertLevel();
    const subDays = this.subDaysRemaining();
    const showSub = subLvl && (!this.subAlertDismissed() || subLvl === 'CRITICAL' || subLvl === 'URGENT');

    const activeList: Array<{
      type: 'DIGITAL_SIGNATURE' | 'SIAT_TOKEN' | 'SUBSCRIPTION';
      level: 'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL';
      days: number;
      weight: number;
    }> = [];

    const weights: Record<string, number> = { CRITICAL: 4, URGENT: 3, WARNING: 2, INFO: 1 };

    if (showCert) activeList.push({ type: 'DIGITAL_SIGNATURE', level: certLvl!, days: certDays!, weight: weights[certLvl!] });
    if (showToken) activeList.push({ type: 'SIAT_TOKEN', level: tokenLvl!, days: tokenDays!, weight: weights[tokenLvl!] });
    if (showSub) activeList.push({ type: 'SUBSCRIPTION', level: subLvl!, days: subDays!, weight: weights[subLvl!] });

    if (activeList.length === 0) return [];

    // Ordenar por severidad descendente, luego por días restantes ascendente
    activeList.sort((a, b) => {
      if (b.weight !== a.weight) return b.weight - a.weight;
      return a.days - b.days;
    });

    return activeList.map(item => {
      if (item.type === 'DIGITAL_SIGNATURE') {
        return this.createCertAlertPayload(item.level, item.days);
      } else if (item.type === 'SIAT_TOKEN') {
        return this.createTokenAlertPayload(item.level, item.days);
      } else {
        return this.createSubAlertPayload(item.level, item.days);
      }
    });
  });

  activeAlert = computed(() => {
    const list = this.activeAlerts();
    return list.length > 0 ? list[0] : null;
  });

  hasCriticalAlert = computed(() => {
    return this.activeAlerts().some(alert => alert.level === 'CRITICAL');
  });

  hasNonDismissibleAlert = computed(() => {
    return this.activeAlerts().some(alert => !alert.isDismissible);
  });

  private createCertAlertPayload(level: 'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL', days: number) {
    const compName = this.validatorsService.company()?.companyName || 'mi empresa';
    const nit = this.validatorsService.company()?.nit ? ` (NIT: ${this.validatorsService.company()?.nit})` : '';
    const phone = this.validatorsService.user()?.businessWhatsapp || '59179746003';
    const dateStr = this.certEndDate() ? this.formatDate(this.certEndDate()!) : '';

    let text = '';
    if (level === 'CRITICAL') {
      text = `Hola, mi empresa ${compName}${nit} tiene su Certificado de Firma Digital vencido y requiero asistencia para renovarlo e instalarlo ante el SIAT.`;
    } else if (level === 'URGENT') {
      text = `Hola, mi empresa ${compName}${nit} tiene su Firma Digital por vencer en ${days} día(s) (el ${dateStr}) y requiero asistencia para la renovación de firma electrónica.`;
    } else {
      text = `Hola, mi empresa ${compName}${nit} tiene su Firma Digital por vencer en ${days} días y deseo coordinar su renovación ante ADSIB/proveedor.`;
    }

    return {
      type: 'DIGITAL_SIGNATURE' as const,
      level,
      daysRemaining: days,
      expirationDate: this.certEndDate(),
      whatsappUrl: `https://wa.me/${phone}?text=${encodeURIComponent(text)}`,
      isDismissible: level === 'WARNING' || level === 'INFO'
    };
  }

  private createTokenAlertPayload(level: 'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL', days: number) {
    const compName = this.validatorsService.company()?.companyName || 'mi empresa';
    const nit = this.validatorsService.company()?.nit ? ` (NIT: ${this.validatorsService.company()?.nit})` : '';
    const phone = this.validatorsService.user()?.businessWhatsapp || '59179746003';
    const dateStr = this.siatTokenEndDate() ? this.formatDate(this.siatTokenEndDate()!) : '';

    let text = '';
    if (level === 'CRITICAL') {
      text = `Hola, mi empresa ${compName}${nit} tiene su Token SIAT vencido y requiero soporte para actualizarlo ante el SIN.`;
    } else if (level === 'URGENT') {
      text = `Hola, mi empresa ${compName}${nit} tiene su Token SIAT por vencer en ${days} día(s) (el ${dateStr}) y requiero asistencia para delegar el nuevo token ante el SIN.`;
    } else {
      text = `Hola, mi empresa ${compName}${nit} tiene su Token SIAT por vencer en ${days} días y requiero asistencia para renovarlo ante el SIN.`;
    }

    return {
      type: 'SIAT_TOKEN' as const,
      level,
      daysRemaining: days,
      expirationDate: this.siatTokenEndDate(),
      whatsappUrl: `https://wa.me/${phone}?text=${encodeURIComponent(text)}`,
      isDismissible: level === 'WARNING' || level === 'INFO'
    };
  }

  private createSubAlertPayload(level: 'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL', days: number) {
    const compName = this.validatorsService.company()?.companyName || 'mi empresa';
    const nit = this.validatorsService.company()?.nit ? ` (NIT: ${this.validatorsService.company()?.nit})` : '';
    const phone = this.validatorsService.user()?.businessWhatsapp || '59179746003';
    const dateStr = this.subExpirationDate() ? this.formatDate(this.subExpirationDate()!) : '';

    let text = '';
    if (level === 'CRITICAL') {
      text = `Hola, mi cuenta ${compName}${nit} se encuentra ${this.isSuspended() ? 'suspendida' : 'vencida'} por falta de pago y deseo regularizar mi suscripción.`;
    } else if (level === 'URGENT') {
      text = `Hola, mi cuenta ${compName}${nit} vence en ${days} día(s) (el ${dateStr}) y deseo renovar mi suscripción con urgencia.`;
    } else {
      text = `Hola, mi cuenta ${compName}${nit} vence en ${days} días y deseo gestionar la renovación de mi suscripción.`;
    }

    return {
      type: 'SUBSCRIPTION' as const,
      level,
      daysRemaining: days,
      expirationDate: this.subExpirationDate(),
      whatsappUrl: `https://wa.me/${phone}?text=${encodeURIComponent(text)}`,
      isDismissible: level === 'WARNING' || level === 'INFO'
    };
  }

  private formatDate(d: Date): string {
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${d.getFullYear()}`;
  }

  openRefreshTokenModal(): void {
    this.profileService.showModalToken = true;
  }

  closeAlert(type: 'DIGITAL_SIGNATURE' | 'SIAT_TOKEN' | 'SUBSCRIPTION'): void {
    if (type === 'DIGITAL_SIGNATURE') {
      this.certAlertDismissed.set(true);
    } else if (type === 'SIAT_TOKEN') {
      this.siatTokenAlertDismissed.set(true);
    } else if (type === 'SUBSCRIPTION') {
      this.subAlertDismissed.set(true);
    }
  }

  closeActiveAlert(): void {
    const current = this.activeAlert();
    if (current) this.closeAlert(current.type);
  }

  toggleThemeMode(event?: MouseEvent) {
    const current = this.currentTheme();
    const nextMode = current === 'dark' ? 'light' : 'dark';

    const applyTheme = () => {
      this.currentTheme.set(nextMode);
      this.eventService.broadcast('changeMode', nextMode);
      const link = document.getElementById('theme-css') as HTMLLinkElement;
      const theme = nextMode === 'dark' ? 'lara-dark-blue' : 'saga-blue';
      if (link) {
        link.href = `assets/themes/${theme}/theme.css`;
      }
      this.eventService.theme.set(nextMode);
      localStorage.setItem('themeMode', nextMode);
    };

    const doc = document as any;
    const isAppearanceTransition =
      doc.startViewTransition !== undefined &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!isAppearanceTransition || !event) {
      applyTheme();
      return;
    }

    const x = event.clientX;
    const y = event.clientY;
    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    );

    const transition = doc.startViewTransition(() => {
      applyTheme();
    });

    transition.ready.then(() => {
      const clipPath = [
        `circle(0px at ${x}px ${y}px)`,
        `circle(${endRadius}px at ${x}px ${y}px)`
      ];
      document.documentElement.animate(
        {
          clipPath: clipPath
        },
        {
          duration: 450,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
          pseudoElement: '::view-transition-new(root)'
        }
      );
    });
  }

  ngOnInit() {
    this.profileService.loadCertificate(this.validatorsService.company());
    const logoStorage = localStorage.getItem('logo');

    if (logoStorage && logoStorage.trim() !== '' && logoStorage !== 'null' && logoStorage !== 'undefined') {
      this.logoUrl = logoStorage;
    } else {
      this.logoUrl = null;
    }

    this.openMobileMenu = false;
    this.element = document.documentElement;
    this.cookieValue = localStorage.getItem('lang');
    const val = this.listLang.filter((x) => x.lang === this.cookieValue);
    this.countryName = val.map((element) => element.text);
    if (val.length === 0) {
      if (this.flagvalue === undefined) {
        this.valueset = 'assets/images/flags/us.jpg';
      }
    } else {
      this.flagvalue = val.map((element) => element.flag);
    }
    this.mediaQuery();
    this.getAllSucursales();
    this.isReloadSub$ =
      this.validatorsService.reload_sucursal_storages$.subscribe((resp) => {
        this.getAllSucursales();
      });

    this.eventService.subscribe('changeMode', (mode: string) => {
      this.currentTheme.set(mode);
    });
  }

  get getUserName(): string {
    const user = this.validatorsService.user();
    if (!user) return '';
    return `${user.fullName || ''} ${user.lastName || ''}`.trim();
  }

  get userInitials(): string {
    const user = this.validatorsService.user();
    if (!user) return 'U';
    const first = user.fullName ? user.fullName.trim().charAt(0).toUpperCase() : '';
    const last = user.lastName ? user.lastName.trim().charAt(0).toUpperCase() : '';
    const initials = `${first}${last}`.trim();
    return initials.length > 0 ? initials : (first || 'U');
  }

  get showLogoImage(): boolean {
    return !!this.logoUrl && this.logoUrl.trim() !== '' && this.logoUrl !== 'null' && this.logoUrl !== 'undefined' && !this.hasLogoError;
  }

  onLogoError(): void {
    this.hasLogoError = true;
  }

  listLang = [
    { text: 'Español', flag: 'assets/images/flags/spain.jpg', lang: 'es' },
    { text: 'English', flag: 'assets/images/flags/us.jpg', lang: 'en' },
  ];

  //** Asignar sucursales al selector, segun solo las sucursales asignadas al usuario logueado */
  getAllSucursales() {
    this.sucursales.set(this.validatorsService.listBranch());
    const id_sucursal = sessionStorage.getItem('id_sucursal');
    const findSucursal = this.sucursales()?.find(
      (resp) => resp.id === id_sucursal
    );
    if (id_sucursal && findSucursal) {
      this.form.get('id_sucursal')?.setValue(id_sucursal);
      this.setStoragesBySucursal(false);
    } else {
      if (this.sucursales()?.length > 0) {
        this.form
          .get('id_sucursal')
          ?.setValue(this.sucursales()[0]?.id ?? 0);
        sessionStorage.setItem(
          'id_sucursal',
          this.form.get('id_sucursal')?.value
        );
        this.setStoragesBySucursal(false);
      }
    }
    this.validatorsService.reload.set(false);
    this.validatorsService.id_sucursal.set(this.form.get('id_sucursal')?.value);
  }

  isExpired(expireDate: string): boolean {
    const now = new Date().getTime();
    const expire = new Date(expireDate).getTime();
    return expire <= now;
  }

  generateNewCufd() {
    const idPointOfSale: string | null = localStorage.getItem('pointOfSaleId');
    if (!idPointOfSale) {
      Swal.fire({
        icon: 'warning',
        title: 'Punto de venta no seleccionado',
        text: 'Por favor, selecciona un punto de venta antes de generar un nuevo CUFD.',
        confirmButtonText: 'Entendido'
      });
      return;
    }

    this.pointOfSaleService.generateCufd(idPointOfSale as sId)
      .subscribe((resp) => {
        const formattedDate = this.datePipe.transform(resp.expireCufd, 'dd-MM-yyyy HH:mm:ss');
        const dataCufd = {
          expireCufd: resp.expireCufd,
          createdCufd: resp.createdAt,
          cufd: resp.cufd,
          controlCode: resp.controlCode,
        };
        localStorage.setItem('dataCufd', JSON.stringify(dataCufd));
        this.authService.updateCufd(dataCufd);
        this.validatorsService.showToast(
          'success',
          'Éxito!',
          resp.message.trim() + ' ' + formattedDate
        );
      });
  }


  setStoragesBySucursal(reload: boolean = true) {
    this.form.markAllAsTouched();
    if (!this.form.valid) return;
    if (reload) {
      this.validatorsService.reload.set(true);
      setTimeout(() => {
        this.validatorsService.reload.set(false);
      }, 100);
    }
    const id_sucursal = this.form.get('id_sucursal')?.value;
    if (id_sucursal) {
      this.validatorsService.id_sucursal.set(id_sucursal);
      sessionStorage.setItem('id_sucursal', id_sucursal);
      const sucursal_select = this.sucursales().find(
        (sucursal) => sucursal.id == id_sucursal
      );
      this.parametricService.getPointsOfSale(sucursal_select!.id).subscribe({
        next: (resp) => this.validatorsService.pointsOfSales.set(resp),
        error: (err) => this.validatorsService.pointsOfSales.set([]),
      });
    } else {
      this.validatorsService.pointsOfSales.set([]);
    }
  }

  mediaQuery() {
    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small])
      .subscribe((state: BreakpointState) => {
        this.isPageViewMovil = state.matches;
        if (this.isPageViewMovil) {
          this.styleSucursales.set({ maxWidth: '130px', minWidth: '130px' });
        } else {
          this.styleSucursales.set({ maxWidth: 'auto', minWidth: '200px' });
        }
      });
  }

  setLanguage(text: string, lang: string, flag: string) {
    this.countryName = text;
    this.flagvalue = flag;
    this.cookieValue = lang;
    this.languageService.setLanguage(lang);
  }

  /**
   * Toggles the right sidebar
   */
  toggleRightSidebar() {
    this.settingsButtonClicked.emit();
  }

  /**
   * Toggle the menu bar when having mobile screen
   */
  toggleMobileMenu(event: any) {
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  /**
   * Logout the user
   */
  logout() {
    this.authService.logout();
  }

  /**
   * Fullscreen method
   */
  fullscreen() {
    document.body.classList.toggle('fullscreen-enable');
    if (
      !document.fullscreenElement &&
      !(document as any).mozFullScreenElement &&
      !(document as any).webkitFullscreenElement
    ) {
      if (this.element.requestFullscreen) {
        this.element.requestFullscreen();
      } else if (this.element.mozRequestFullScreen) {
        /* Firefox */
        this.element.mozRequestFullScreen();
      } else if (this.element.webkitRequestFullscreen) {
        /* Chrome, Safari and Opera */
        this.element.webkitRequestFullscreen();
      } else if (this.element.msRequestFullscreen) {
        /* IE/Edge */
        this.element.msRequestFullscreen();
      }
    } else {
      if (this.document.exitFullscreen) {
        this.document.exitFullscreen();
      } else if ((this.document as any).mozCancelFullScreen) {
        /* Firefox */
        (this.document as any).mozCancelFullScreen();
      } else if ((this.document as any).webkitExitFullscreen) {
        /* Chrome, Safari and Opera */
        (this.document as any).webkitExitFullscreen();
      } else if ((this.document as any).msExitFullscreen) {
        /* IE/Edge */
        (this.document as any).msExitFullscreen();
      }
    }
  }
}
